import { runtimeTarget } from '@/lib/runtime-target'

// desktop static export (Tauri) needs .html URLs, but dev mode uses Next.js
// dev server which doesn't serve .html routes. NODE_ENV is always 'development'
// in `next dev`, regardless of build-time env vars, so it's safe to use here.
const isStaticExport = runtimeTarget === 'desktop' && process.env.NODE_ENV === 'production'

type AppRouter = {
  push: (href: string) => void
  replace: (href: string) => void
}

type NavigateMode = 'push' | 'replace'

function splitRoute(route: string) {
  const hashIndex = route.indexOf('#')
  const routeWithoutHash = hashIndex >= 0 ? route.slice(0, hashIndex) : route
  const hash = hashIndex >= 0 ? route.slice(hashIndex) : ''
  const queryIndex = routeWithoutHash.indexOf('?')
  const pathname =
    queryIndex >= 0 ? routeWithoutHash.slice(0, queryIndex) : routeWithoutHash
  const search = queryIndex >= 0 ? routeWithoutHash.slice(queryIndex) : ''

  return {
    pathname: pathname || '/',
    search,
    hash,
  }
}

function hasFileExtension(pathname: string) {
  const lastSegment = pathname.split('/').pop() ?? ''
  return /\.[a-z0-9]+$/i.test(lastSegment)
}

export function toDocumentHref(route: string) {
  if (!isStaticExport) {
    return route
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(route) || route.startsWith('//')) {
    return route
  }

  const { pathname, search, hash } = splitRoute(route)
  if (hasFileExtension(pathname)) {
    return `${pathname}${search}${hash}`
  }

  const normalizedPathname =
    pathname === '/' ? '/index' : pathname.replace(/\/$/, '')
  return `${normalizedPathname}.html${search}${hash}`
}

function normalizeRoutePath(pathname: string) {
  if (pathname === '/index.html') {
    return '/'
  }
  return pathname.endsWith('.html') ? pathname.slice(0, -5) : pathname
}

export function isCurrentAppRoute(route: string) {
  if (typeof window === 'undefined') {
    return false
  }

  const { pathname } = splitRoute(route)
  return normalizeRoutePath(window.location.pathname) === normalizeRoutePath(pathname)
}

export function navigateToAppRoute(
  router: AppRouter,
  route: string,
  mode: NavigateMode = 'push',
) {
  if (isStaticExport) {
    window.location.href = toDocumentHref(route)
    return
  }

  router[mode](route)
}

export function redirectToAppRoute(route: string) {
  window.location.href = toDocumentHref(route)
}
